import { useState, useEffect, useReducer, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { apiAgent } from '../../../axiosConfig'
import { InputBox } from '../../../design_system/components/Input Fields/InputBox.jsx'
import { Badge } from '../../../design_system/components/Input Fields/Badge.jsx'
import { SelectBox } from '../../../design_system/components/Input Fields/SelectBox.jsx'
import { SelectionControl } from '../../../design_system/components/SelectionControls/SelectionControl.jsx'
import { Loader } from '../../../design_system/components/Loader.jsx'
import { Icons } from '../../../design_system/components/Icons'
import { IconButton } from '../../../design_system/components/Button/Button.jsx'
import { Tooltip } from '../../../design_system/components/ToolTip/ToolTip.jsx'
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx'
import { updateCurrentTeam } from '../../../redux/currentTeamSlice'
import { useSettingsContext, settingsSnapshot } from './SettingsContext'
import { TEAM_TYPES, getTeamType, canSwitchTeamType, usesRevertDate } from './teamTypes'
import { ConfirmChangePopup } from './ConfirmChangePopup'
import useCheckUserAccess from '../../custom_hooks/useCheckUserAccess'
import { CommonRangeFilter } from '../../admin_views/filters/CommonRangeFilter'
import { SingleDateFilterDropdown } from '../../admin_views/teams/singleDateFilter.js'
import { dateFormator, datepicker_input_format, getDateUtil } from '../../utils/dateUtils'

// ── Constants ─────────────────────────────────────────────────────────────────
const AUTO_PUBLISH_CONDITION_TYPES = [
    { key: 'Above',   label: 'Above or equal' },
    { key: 'Below',   label: 'Below or equal' },
    { key: 'Between', label: 'Between' },
]

const AUTO_PUBLISH_TYPES = [
    { type: 'news', label: 'News Results', iconName: 'comment', colorKey: 'news' },
    { type: 'sec',  label: 'SEC filings',  iconName: 'notes',   colorKey: 'sec'  },
]

let _rangeUid = 0
const nextRangeUid = () => `range_${++_rangeUid}`

const DEFAULT_RANGE = { min: '', max: '', tags: '', tags_excluded: '', cond: 'Above', label: 'Above or equal' }

const rangeCondFor = (min, max) => {
    if (!max) return { cond: 'Above', label: 'Above or equal' }
    if (!min) return { cond: 'Below', label: 'Below or equal' }
    return { cond: 'Between', label: 'Between' }
}

const parseSavedConditions = (conditions) => (conditions || []).map(c => ({
    _uid: nextRangeUid(), min: c.min_score, max: c.max_score,
    tags: c.tags || '', tags_excluded: c.tags_excluded || '', ...rangeCondFor(c.min_score, c.max_score),
}))

const seedDefaultConditions = (conditions) => (conditions || []).map(c => ({
    _uid: nextRangeUid(), min: c.min_score, max: c.max_score,
    tags: (c.tags || []).join(', '), tags_excluded: (c.tags_excluded || []).join(', '),
    ...rangeCondFor(c.min_score, c.max_score),
}))

const emptyAutoPublish = () =>
    AUTO_PUBLISH_TYPES.reduce((m, { type }) => { m[type] = { enabled: false, conditions: [], limit: '' }; return m }, {})

const HOVER_CARD_OPTIONS = [
    { label: 'Default fields', options: [
        { label: 'Unique Id',            key: 'external_id' },
        { label: 'Full Name',            key: 'full_name' },
        { label: 'Spouse Name',          key: 'spouse_name' },
        { label: 'Other Companies',      key: 'custom_companies' },
        { label: 'Skills / Occupation',  key: 'skills' },
        { label: 'Education',            key: 'college' },
        { label: 'Course',               key: 'course' },
        { label: 'Branch',               key: 'branch' },
        { label: 'Graduation Year',      key: 'graduation_year' },
        { label: 'City',                 key: 'city' },
        { label: 'State',                key: 'state' },
        { label: 'Country',              key: 'country' },
        { label: 'Location',             key: 'location' },
        { label: 'Linkedin URL',         key: 'linkedin_url' },
        { label: 'Alias Names',          key: 'alias_names' },
        { label: 'Assigned Team members',key: 'assigns' },
        { label: 'Relationships',        key: 'relationships' },
        { label: 'Assigned Tags',        key: 'tags' },
    ]},
]

const EXPORT_FIELD_OPTIONS = [
    { label: 'Spouse Name',          key: 'spouse_name' },
    { label: 'Other Companies',      key: 'custom_companies' },
    { label: 'Skills / Occupation',  key: 'skills' },
    { label: 'Education',            key: 'college' },
    { label: 'Course',               key: 'course_name' },
    { label: 'Branch',               key: 'branch_name' },
    { label: 'Graduation Year',      key: 'graduation_year' },
    { label: 'City',                 key: 'city' },
    { label: 'State',                key: 'state' },
    { label: 'Country',              key: 'country' },
    { label: 'Location',             key: 'location' },
    { label: 'Alias Names',          key: 'alias_names' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────
const hasRangeOverlap = (conditions) => {
    const sorted = conditions
        .map(c => ({ min: c.min ? parseFloat(c.min) : 0, max: c.max ? parseFloat(c.max) : Infinity }))
        .sort((a, b) => a.min - b.min)
    for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].max > sorted[i + 1].min) return true
    }
    return false
}

const parseTagList = (tags) =>
    (tags || '').split(',').map(t => t.trim().toLowerCase()).filter(Boolean)

const tagRuleError = (rules, conditions) => {
    const lc = (arr) => (arr || []).map(t => t.toLowerCase())
    const required          = lc(rules?.required_tags)
    const forbidden         = lc(rules?.forbidden_tags)
    const forbiddenExcluded = lc(rules?.forbidden_excluded)
    for (const c of conditions) {
        const tags     = parseTagList(c.tags)
        const excluded = parseTagList(c.tags_excluded)
        for (const tag of required) {
            if (!tags.includes(tag)) return `every rule must include the "${tag}" tag`
        }
        for (const tag of forbiddenExcluded) {
            if (excluded.includes(tag)) return `rules cannot exclude the "${tag}" tag`
        }
        for (const tag of forbidden) {
            if (tags.includes(tag) || excluded.includes(tag)) return `rules cannot use the "${tag}" tag`
        }
    }
    return null
}

// ── Reducer ───────────────────────────────────────────────────────────────────

const initialState = {
    teamType:                     'unverified',
    trial_expiring_on:            null,
    trialDateObj:                 null,
    max_prospect_limit:           '',
    score_threshold:              '',
    mid_score_threshold:          '',
    organization_score_threshold: '',
    organization_mid_score_threshold: '',
    max_limit_per_day:            '',
    auto_publish:                 emptyAutoPublish(),
    default_auto_publish:         {},
    auto_publish_tag_rules:       {},
    hover_card_enabled:           false,
    short_profile_fields:         [],    // [{key, label}]
    export_fields_enabled:        false,
    newsfeed_export_fields:       [],    // [{key, label}]
    custom_fields:                [],
}

const setAutoPublishType = (state, type, changes) => ({
    ...state,
    auto_publish: { ...state.auto_publish, [type]: { ...state.auto_publish[type], ...changes } },
})

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET':        return { ...state, ...action.payload }
        case 'SET_TYPE':   return setAutoPublishType(state, action.articleType, action.payload)
        case 'SET_RANGE': {
            const conditions = [...state.auto_publish[action.articleType].conditions]
            conditions[action.index] = action.payload
            return setAutoPublishType(state, action.articleType, { conditions })
        }
        case 'REMOVE_RANGE': {
            const conditions = state.auto_publish[action.articleType].conditions.filter((_, i) => i !== action.index)
            return setAutoPublishType(state, action.articleType, {
                conditions,
                enabled: conditions.length > 0 ? state.auto_publish[action.articleType].enabled : false,
            })
        }
        default: return state
    }
}

// ── Sub-components ────────────────────────────────────────────────────────────

const TeamTypeCard = ({ type, selected, onSelect, disabled }) => (
    <button
        className={`team-type-card${selected ? ' team-type-card--selected' : ''}${disabled ? ' team-type-card--disabled' : ''}`}
        onClick={() => !disabled && onSelect(type.key)}
        type="button"
        disabled={disabled}
    >
        <div className={`team-type-card__icon team-type-card__icon--${type.key}`}>
            <Icons name={type.iconName} size={16} />
        </div>
        <span className="body-m-bold team-type-card__label">{type.label}</span>
        <span className="body-s team-type-card__subtitle">{type.subtitle}</span>
    </button>
)

const HoverCardField = ({ field, onRemove }) => (
    <div className="hover-field-tag body-s-semibold">
        {field.label}
        <button className="hover-field-tag__remove" onClick={() => onRemove(field.key)} type="button">
            <Icons name="close" size={16} />
        </button>
    </div>
)

const AutoPublishTypeCard = ({ config, setting, onLimitChange, onToggle, onSetRange, onRemoveRange, onAddRange }) => {
    const [expanded, setExpanded]   = useState(false)
    const [showRanges, setShowRanges] = useState(false)
    const { type, label, iconName, colorKey } = config
    const enabled = setting.enabled

    return (
        <div className={`ntf-card${expanded ? ' ntf-card--expanded' : ''}`}>
            <button type="button" className="ntf-card__head" onClick={() => setExpanded(v => !v)}>
                <span className={`ntf-card__icon ntf-card__icon--${colorKey}`}><Icons name={iconName} size={16} /></span>
                <span className="ntf-card__head-text">
                    <span className="ntf-card__title body-s-semibold">{label}</span>
                    <span className="ntf-card__summary">
                        <Badge label={enabled ? 'Enabled' : 'Disabled'} type={enabled ? 'Success' : 'Error'} fontClass="support-m-semibold" />
                    </span>
                </span>
                <Icons name={expanded ? 'chevron-up' : 'chevron-down'} size={20} />
            </button>

            {expanded && (
                <div className="ntf-card__body">
                    <div className="settings-panel__item">
                        <div className="settings-panel__item-info">
                            <span className="body-m-semibold settings-panel__item-title">Enable auto publish</span>
                            <span className="body-s-semibold settings-panel__item-desc">
                                Configure score-based rules for automatic content publishing
                            </span>
                        </div>
                        <div className="settings-panel__item-action">
                            <SelectionControl type="toggle" checked={enabled} onChange={e => onToggle(type, e.target.checked)} />
                        </div>
                    </div>

                    {enabled && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-m)', marginBottom: 'var(--space-m)' }}>
                                <span className="input-box-label body-s-semibold" style={{ marginBottom: 0 }}>
                                    Max articles auto-published per prospect per day (0 = unlimited)
                                </span>
                                <InputBox
                                    Size="Large"
                                    Type="Number"
                                    value={setting.limit ?? ''}
                                    handleOnChange={e => onLimitChange(type, e.target.value)}
                                    placeholder="0"
                                    inputStyle={{ maxWidth: 70 }}
                                />
                            </div>
                            <button className="config-show-range body-m-semibold" onClick={() => setShowRanges(v => !v)} type="button">
                                {showRanges ? 'Hide range' : 'Show range'}
                                <Icons name={showRanges ? 'chevron-up' : 'chevron-down'} size={16} />
                            </button>

                            {showRanges && (
                                <div className="config-ranges">
                                    {setting.conditions.map((range, i) => (
                                        <div key={range._uid} className="range-card">
                                            <div className="range-card__header">
                                                <span className="body-m-semibold color-text-secondary">Range {i + 1}</span>
                                                <Tooltip tooltipText="Remove range" placement="bottom-end" showWithoutOverflowing>
                                                    <IconButton
                                                        Size="ExtraSmall"
                                                        Type="DangerTertiary"
                                                        IconName="delete"
                                                        ariaLabel="Remove range"
                                                        onClick={() => onRemoveRange(type, i)}
                                                    />
                                                </Tooltip>
                                            </div>
                                            <div className="range-card__body">
                                                <div className="range-card__score-row">
                                                    <CommonRangeFilter
                                                        conditions={AUTO_PUBLISH_CONDITION_TYPES}
                                                        layout="Horizontal"
                                                        Size="Large"
                                                        range_filter={range}
                                                        setRangeFilter={val => onSetRange(type, i, val)}
                                                        customLabel={
                                                            <div className="flex items-center gap-xs cursor-pointer">
                                                                Score threshold
                                                                <Tooltip
                                                                    tooltipText="Below or Equal includes the max edge. Between excludes both edges. Above or Equal includes the min edge."
                                                                    showWithoutOverflowing={true}
                                                                    placement="bottom"
                                                                >
                                                                    <Icons name="info" size={16} />
                                                                </Tooltip>
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                                <InputBox
                                                    inputStyle={{ maxWidth: 480 }}
                                                    label="Contains tags (Comma separated)"
                                                    showLabel
                                                    value={range.tags}
                                                    handleOnChange={e => onSetRange(type, i, { ...range, tags: e.target.value })}
                                                    placeholder="Placeholder"
                                                />
                                                <InputBox
                                                    inputStyle={{ maxWidth: 480 }}
                                                    label="Doesn't contain tags (Comma separated)"
                                                    showLabel
                                                    value={range.tags_excluded}
                                                    handleOnChange={e => onSetRange(type, i, { ...range, tags_excluded: e.target.value })}
                                                    placeholder="Placeholder"
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    <button className="config-add-range body-s-semibold" onClick={() => onAddRange(type)} type="button">
                                        Add a new range
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

// ── Main component ────────────────────────────────────────────────────────────

// Keys compared for change detection (excludes derived/static fields)
const COMPARABLE_KEYS = [
    'trialDateObj', 'max_prospect_limit', 'score_threshold',
    'mid_score_threshold', 'organization_score_threshold', 'organization_mid_score_threshold',
    'max_limit_per_day', 'auto_publish',
    'hover_card_enabled', 'short_profile_fields',
    'export_fields_enabled', 'newsfeed_export_fields',
]

const stateSnapshot = (s) => {
    const snap = {}
    for (const k of COMPARABLE_KEYS) snap[k] = s[k]
    // Per-type auto-publish: ignore a disabled type's ranges; strip _uid render keys
    snap.auto_publish = AUTO_PUBLISH_TYPES.reduce((m, { type }) => {
        const t = s.auto_publish[type] || { enabled: false, conditions: [], limit: '' }
        m[type] = {
            enabled: t.enabled,
            limit: t.limit ?? '',
            conditions: t.enabled ? t.conditions.map(({ _uid, ...rest }) => rest) : [],
        }
        return m
    }, {})
    if (!snap.hover_card_enabled) snap.short_profile_fields = []
    if (!snap.export_fields_enabled) snap.newsfeed_export_fields = []
    // Date → timestamp (normalize() in SettingsContext flattens Date to {})
    // Also ignore the trial date when the team type doesn't use it
    const usesTrialDate = usesRevertDate(s.teamType)
    snap.trialDateObj = usesTrialDate && snap.trialDateObj instanceof Date
        ? snap.trialDateObj.getTime()
        : null
    return settingsSnapshot(snap)
}

const ConfigurationTab = () => {
    const current_team = useSelector(s => s.current_team.team)
    const [state, localDispatch] = useReducer(reducer, initialState)
    const [isDataLoaded, setIsDataLoaded]   = useState(false)
    const [originalTeamType, setOriginalTeamType] = useState(null)
    const [isConfirming, setIsConfirming]   = useState(false)
    const [pendingTeamType, setPendingTeamType]     = useState(null)   // non-null ⇒ confirm popup open
    const [dateOpen, setDateOpen]           = useState(false)
    const [rangeError, setRangeError]       = useState('')
    const checkUserAccess = useCheckUserAccess()
    const toast    = useToast()
    const dispatch = useDispatch()
    const { registerTab, unregisterTab, setHasChanges } = useSettingsContext()
    const snapshotRef = useRef(null)

    const set = (payload) => localDispatch({ type: 'SET', payload })

    // ── Fetch ─────────────────────────────────────────────────────────────────

    useEffect(() => {
        const teamReq = apiAgent({ method: 'get', url: '/team/team_details', params: {} })
        const fieldsReq = apiAgent({ method: 'get', url: '/team/custom_fields', params: {} })

        Promise.all([teamReq, fieldsReq])
            .then(([teamRes, fieldsRes]) => {
                const d = teamRes.data.data
                const customFields = (fieldsRes.data.custom_fields || []).map(f => ({ key: f.id, label: f.field_name }))

                const savedSettings = d.auto_publish_settings || []
                const autoPublish = AUTO_PUBLISH_TYPES.reduce((m, { type }) => {
                    const s = savedSettings.find(x => x.article_type === type)
                    const limit = s?.publishable_articles_limit_per_prospect_per_day
                    m[type] = {
                        enabled: !!s?.enabled,
                        conditions: parseSavedConditions(s?.conditions),
                        limit: limit != null ? String(limit) : '',
                    }
                    return m
                }, {})
                const defaultAutoPublish = (d.default_auto_publish_conditions || []).reduce((m, s) => {
                    m[s.article_type] = s.conditions || []
                    return m
                }, {})
                const autoPublishTagRules = (d.default_auto_publish_conditions || []).reduce((m, s) => {
                    m[s.article_type] = s.tag_rules || {}
                    return m
                }, {})
                const shortFields = Object.entries(d.short_profile_fields || {}).map(([key, label]) => ({ key, label }))
                const trialDateObj = loadTrialDate(d.trial_expiring_on)

                // Resolve export field keys to {key, label} using both default + custom field lists
                const allExportOptions = [...EXPORT_FIELD_OPTIONS, ...customFields]
                const savedExportKeys = d.newsfeed_export_fields || []
                const exportFields = savedExportKeys
                    .map(key => allExportOptions.find(o => o.key === key))
                    .filter(Boolean)
                setOriginalTeamType(getTeamType(d))
                const loaded = {
                    teamType:                        getTeamType(d),
                    trial_expiring_on:               trialDateObj,
                    trialDateObj,
                    max_prospect_limit:              d.max_prospect_limit != null ? String(d.max_prospect_limit) : '',
                    score_threshold:                 d.score_threshold != null ? String(d.score_threshold) : '',
                    mid_score_threshold:             d.mid_score_threshold != null ? String(d.mid_score_threshold) : '',
                    organization_score_threshold:    d.organization_score_threshold != null ? String(d.organization_score_threshold) : '',
                    organization_mid_score_threshold: d.organization_mid_score_threshold != null ? String(d.organization_mid_score_threshold) : '',
                    max_limit_per_day:               d.max_limit_per_day != null ? String(d.max_limit_per_day) : '',
                    auto_publish:                    autoPublish,
                    default_auto_publish:            defaultAutoPublish,
                    auto_publish_tag_rules:          autoPublishTagRules,
                    hover_card_enabled:              Object.keys(d.short_profile_fields || {}).length > 0,
                    short_profile_fields:            shortFields,
                    export_fields_enabled:           exportFields.length > 0,
                    newsfeed_export_fields:          exportFields,
                    custom_fields:                   customFields,
                }
                set(loaded)
                snapshotRef.current = stateSnapshot(loaded)
                setIsDataLoaded(true)
            })
            .catch(() => toast.error('Failed to load configuration'))
    }, [])

    // ── Date helpers ─────────────────────────────────────────────────────────

    const loadTrialDate = (raw) => {
        if (!raw) return dateFormator(getDateUtil(+30), { type: 'datepicker_object' })
        const parts = raw.toString().split('-')
        return dateFormator(new Date(parts[2], parts[1] - 1, parts[0]), { type: 'datepicker_object' })
    }

    const trialDateDisplay = state.trialDateObj
        ? dateFormator(state.trialDateObj, { type: 'input-format' }, '-')
        : ''

    // ── Team type ────────────────────────────────────────────────────────────
    const handleTeamTypeSelect = (key) => {
        if (key === state.teamType) return
        setPendingTeamType(key)
    }

    // ── Auto publish ─────────────────────────────────────────────────────────

    const handleAutoPublishToggle = (articleType, enabled) => {
        const payload = enabled
            ? { enabled: true, conditions: seedDefaultConditions(state.default_auto_publish[articleType]) }
            : { enabled: false, conditions: [] }
        localDispatch({ type: 'SET_TYPE', articleType, payload })
    }

    // ── Hover cards ──────────────────────────────────────────────────────────

    const allHoverOptions = [
        ...HOVER_CARD_OPTIONS[0].options,
        ...state.custom_fields,
    ]

    const removeHoverField = (key) =>
        set({ short_profile_fields: state.short_profile_fields.filter(f => f.key !== key) })

    const addHoverField = (opt) => {
        if (state.short_profile_fields.length >= 5) return
        if (state.short_profile_fields.some(f => f.key === opt.key)) return
        set({ short_profile_fields: [...state.short_profile_fields, opt] })
    }

    const availableHoverOptions = allHoverOptions.filter(
        o => !state.short_profile_fields.some(f => f.key === o.key)
    )

    // ── Export fields ─────────────────────────────────────────────────────────

    const allExportOptions = [...EXPORT_FIELD_OPTIONS, ...state.custom_fields]

    const removeExportField = (key) =>
        set({ newsfeed_export_fields: state.newsfeed_export_fields.filter(f => f.key !== key) })

    const addExportField = (opt) => {
        if (state.newsfeed_export_fields.length >= 30) return
        if (state.newsfeed_export_fields.some(f => f.key === opt.key)) return
        set({ newsfeed_export_fields: [...state.newsfeed_export_fields, opt] })
    }

    const availableExportOptions = allExportOptions.filter(
        o => !state.newsfeed_export_fields.some(f => f.key === o.key)
    )

    // ── Change detection ──────────────────────────────────────────────────────

    useEffect(() => {
        if (snapshotRef.current === null) return
        const changed = stateSnapshot(state) !== snapshotRef.current
        setHasChanges(changed)
    }, [state, setHasChanges])

    // ── Save ─────────────────────────────────────────────────────────────────

    const handleSubmit = useCallback(() => {
        setRangeError('')
        for (const { type, label } of AUTO_PUBLISH_TYPES) {
            const s = state.auto_publish[type]
            if (!s.enabled) continue
            if (hasRangeOverlap(s.conditions)) {
                setRangeError(`${label}: score ranges cannot overlap. Please adjust the values.`)
                return Promise.reject()
            }
            const tagError = tagRuleError(state.auto_publish_tag_rules[type], s.conditions)
            if (tagError) {
                setRangeError(`${label}: ${tagError}.`)
                return Promise.reject()
            }
        }

        const short_profile_fields = state.hover_card_enabled
            ? state.short_profile_fields.reduce((map, f) => { map[f.key] = f.label; return map }, {})
            : {}

        const trialDateStr = state.trialDateObj
            ? datepicker_input_format(state.trialDateObj)
            : null

        const data = {
            trial_expiring_on:               trialDateStr,
            max_prospect_limit:              state.max_prospect_limit,
            score_threshold:                 state.score_threshold,
            mid_score_threshold:             state.mid_score_threshold,
            organization_score_threshold:    state.organization_score_threshold,
            organization_mid_score_threshold: state.organization_mid_score_threshold,
            max_limit_per_day:               state.max_limit_per_day,
            auto_publish_settings:           AUTO_PUBLISH_TYPES.map(({ type }) => {
                const s = state.auto_publish[type]
                return {
                    article_type: type,
                    enabled: s.enabled,
                    publishable_articles_limit_per_prospect_per_day: s.limit,
                    conditions: s.enabled
                        ? s.conditions.map(({ _uid, cond, label, ...c }) => ({ ...c, min: c.min ?? '', max: c.max ?? '' }))
                        : [],
                }
            }),
            short_profile_fields,
            newsfeed_export_fields:          state.export_fields_enabled
                ? state.newsfeed_export_fields.map(f => f.key)
                : [],
        }

        return apiAgent({ method: 'post', url: '/team/update_configuration', data })
            .then(res => {
                dispatch(updateCurrentTeam(res.data.team))
                toast.success('Configuration saved')
                snapshotRef.current = stateSnapshot(state)
                setHasChanges(false)
            })
            .catch(err => {
                toast.error(err?.response?.data?.error || 'Something went wrong. Please try again.')
            })
    }, [state, dispatch, toast, setHasChanges])

    // Confirm popup → switch team type via its own endpoint (revert date is auto-set by the backend).
    const handleConfirmChange = useCallback(() => {
        setIsConfirming(true)
        apiAgent({ method: 'post', url: '/team/change_plan_type', data: { team_type: pendingTeamType } })
            .then(res => {
                dispatch(updateCurrentTeam(res.data.team))
                toast.success('Team type updated')
                const dateObj = res.data.team.trial_expiring_on ? loadTrialDate(res.data.team.trial_expiring_on) : null
                const planUpdates = { teamType: pendingTeamType, trialDateObj: dateObj }
                if (pendingTeamType === 'premium') planUpdates.max_prospect_limit = ''
                set(planUpdates)
                setOriginalTeamType(pendingTeamType)
                const savedState = snapshotRef.current ? JSON.parse(snapshotRef.current) : state
                snapshotRef.current = stateSnapshot({ ...savedState, ...planUpdates })
            })
            .catch(err => {
                toast.error(err?.response?.data?.error || 'Something went wrong. Please try again.')
            })
            .finally(() => {
                setIsConfirming(false)
                setPendingTeamType(null)
            })
    }, [pendingTeamType, state, dispatch, toast])

    const handleCancel = useCallback(() => {
        if (snapshotRef.current) {
            const saved = JSON.parse(snapshotRef.current)
            // normalize converts '' → null; restore null → '' for controlled inputs
            const numberFields = ['max_prospect_limit', 'score_threshold', 'mid_score_threshold',
                'organization_score_threshold', 'organization_mid_score_threshold', 'max_limit_per_day']
            for (const k of numberFields) { if (saved[k] === null) saved[k] = '' }
            if (saved.trialDateObj) saved.trialDateObj = new Date(saved.trialDateObj)
            if (saved.auto_publish) {
                for (const { type } of AUTO_PUBLISH_TYPES) {
                    const t = saved.auto_publish[type]
                    if (t) t.limit = t.limit ?? ''
                    if (t && Array.isArray(t.conditions)) {
                        t.conditions = t.conditions.map(c => ({
                            ...c,
                            _uid: nextRangeUid(),
                            min: c.min ?? '',
                            max: c.max ?? '',
                            tags: c.tags ?? '',
                            tags_excluded: c.tags_excluded ?? '',
                        }))
                    }
                }
            }
            set(saved)
        }
        setRangeError('')
    }, [])

    // ── Register with settings footer ────────────────────────────────────────

    const submitRef = useRef(handleSubmit)
    const cancelCallbackRef = useRef(handleCancel)
    submitRef.current = handleSubmit
    cancelCallbackRef.current = handleCancel

    useEffect(() => {
        const id = registerTab({
            onSave: (...args) => submitRef.current(...args),
            onCancel: (...args) => cancelCallbackRef.current(...args),
        })
        return () => unregisterTab(id)
    }, [registerTab, unregisterTab])

    const showRevertDate = state.teamType === 'verified' || state.teamType === 'outbound'

    return (
        <div className="settings-panel">
            {pendingTeamType && (
                <ConfirmChangePopup
                    content={{ title: "Change team type?", subtitle: "This will affect team access and matching behaviour." }}
                    switching={{ from: state.teamType, to: pendingTeamType }}
                    isLoading={isConfirming}
                    operations={{ onConfirm: handleConfirmChange, onCancel: () => setPendingTeamType(null) }}
                />
            )}
            <div className="settings-panel__header">
                <span className="body-l-bold settings-panel__title">Configuration</span>
                <span className="body-m-semibold settings-panel__description">
                    Set up the rules, limits, and structures that define how your team operates.
                </span>
            </div>

            {!isDataLoaded ? <Loader isLoading Size="Medium" /> : <>

            {/* ── Team type ──────────────────────────────────────────────── */}
            {checkUserAccess('team_type') && (
                <div className="settings-panel__section">
                    <span className="body-m-bold settings-panel__section-title">Team type</span>
                    <div className="team-type-cards">
                        {TEAM_TYPES.map(t => (
                            <TeamTypeCard
                                key={t.key}
                                type={t}
                                selected={state.teamType === t.key}
                                onSelect={handleTeamTypeSelect}
                                disabled={!canSwitchTeamType(originalTeamType, t.key)}
                            />
                        ))}
                    </div>

                    {showRevertDate && (
                        <div className="mt-m relative inline-block">
                            <InputBox
                                label="Reverting to unverified on"
                                showLabel
                                Size="Large"
                                value={trialDateDisplay || 'dd/mm/yyyy'}
                                readOnly
                                customStyle={{ width: 180, cursor: 'pointer' }}
                                inputStyle={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setDateOpen(v => !v)}
                            />
                            {dateOpen && (
                                <div style={{ position: 'absolute', top: '100%', zIndex: 'var(--z-dropdown)', marginTop: 4 }}>
                                    <SingleDateFilterDropdown
                                        currDate={state.trialDateObj}
                                        setCurrDate={(val) => { set({ trialDateObj: val }); setDateOpen(false) }}
                                        toggleDropDown={() => setDateOpen(false)}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Prospect limit & Score thresholds ──────────────────────── */}
            {checkUserAccess('team_type') && (
                <div className="settings-panel__section">
                    <span className="body-m-bold settings-panel__section-title">
                        Prospect limit &amp; Score thresholds
                    </span>
                    <div className="config-score-grid">
                        <InputBox
                            Size="Large"
                            label="Prospect limit"
                            showLabel
                            type="number"
                            value={state.max_prospect_limit}
                            handleOnChange={e => set({ max_prospect_limit: e.target.value })}
                            placeholder="Unlimited"
                            customStyle={{maxWidth: 260}}
                        />
                        <InputBox
                            Size="Large"
                            label="Mark spam when article count exceeds"
                            showLabel
                            type="number"
                            value={state.max_limit_per_day}
                            handleOnChange={e => set({ max_limit_per_day: e.target.value })}
                            placeholder="No limit"
                            customStyle={{maxWidth: 260}}
                        />
                        <InputBox
                            Size="Large"
                            label="Individual high score threshold"
                            showLabel
                            type="number"
                            value={state.score_threshold}
                            handleOnChange={e => set({ score_threshold: e.target.value })}
                            placeholder="—"
                            customStyle={{maxWidth: 260}}
                        />
                        <InputBox
                            Size="Large"
                            label="Organization high score threshold"
                            showLabel
                            type="number"
                            value={state.organization_score_threshold}
                            handleOnChange={e => set({ organization_score_threshold: e.target.value })}
                            placeholder="—"
                            customStyle={{maxWidth: 260}}
                        />
                        <InputBox
                            Size="Large"
                            label="Individual mid score threshold"
                            showLabel
                            type="number"
                            value={state.mid_score_threshold}
                            handleOnChange={e => set({ mid_score_threshold: e.target.value })}
                            placeholder="—"
                            customStyle={{maxWidth: 260}}
                        />
                        <InputBox
                            Size="Large"
                            label="Organization mid score threshold"
                            showLabel
                            type="number"
                            value={state.organization_mid_score_threshold}
                            handleOnChange={e => set({ organization_mid_score_threshold: e.target.value })}
                            placeholder="—"
                            customStyle={{maxWidth: 260}}
                        />
                    </div>
                </div>
            )}

            {/* ── Auto publishing ───────────────────────────────────────── */}
            {checkUserAccess('auto_publishing_fields') && (
                <div className="settings-panel__section">
                    <span className="body-m-bold settings-panel__section-title">Auto publishing</span>
                    <div className="config-ap-cards">
                        {AUTO_PUBLISH_TYPES.filter(({ type }) => {
                            if (type === 'sec') return current_team?.sec_filings_enabled
                            return true
                        }).map(cfg => (
                            <AutoPublishTypeCard
                                key={cfg.type}
                                config={cfg}
                                setting={state.auto_publish[cfg.type]}
                                onLimitChange={(t, v) => localDispatch({ type: 'SET_TYPE', articleType: t, payload: { limit: v } })}
                                onToggle={handleAutoPublishToggle}
                                onSetRange={(t, i, val) => localDispatch({ type: 'SET_RANGE', articleType: t, index: i, payload: val })}
                                onRemoveRange={(t, i) => localDispatch({ type: 'REMOVE_RANGE', articleType: t, index: i })}
                                onAddRange={t => localDispatch({ type: 'SET_TYPE', articleType: t, payload: { conditions: [...state.auto_publish[t].conditions, { ...DEFAULT_RANGE, _uid: nextRangeUid() }] } })}
                            />
                        ))}
                    </div>

                    {rangeError && (
                        <span className="helper-text support-m color-text-error">{rangeError}</span>
                    )}
                </div>
            )}

            {/* ── Profile hover cards ───────────────────────────────────── */}
            {checkUserAccess('short_profile_data_fields') && (
                <div className="settings-panel__section">
                    <span className="body-m-bold settings-panel__section-title">Profile hover cards</span>
                    <div className="settings-panel__item">
                        <div className="settings-panel__item-info">
                            <span className="body-m-semibold settings-panel__item-title">
                                Enable profile hover card
                            </span>
                            <span className="body-s settings-panel__item-desc">
                                Choose up to 5 fields to display on the prospect's profile hover card
                            </span>
                        </div>
                        <div className="settings-panel__item-action">
                            <SelectionControl
                                type="toggle"
                                checked={state.hover_card_enabled}
                                onChange={e => set({ hover_card_enabled: e.target.checked })}
                            />
                        </div>
                    </div>

                    {state.hover_card_enabled && (
                        <div className="hover-fields">
                            <div className="hover-fields__tags">
                                {state.short_profile_fields.map(f => (
                                    <HoverCardField key={f.key} field={f} onRemove={removeHoverField} />
                                ))}
                            </div>
                            {state.short_profile_fields.length < 5 && (
                                <SelectBox
                                    Size="Large"
                                    value={undefined}
                                    placeholder={availableHoverOptions.length === 0 ? "No more fields" : "Select a field to add"}
                                    options={availableHoverOptions.map(o => ({ label: o.label, value: o.key }))}
                                    handleOnChange={(val) => {
                                        const opt = availableHoverOptions.find(o => o.key === val)
                                        if (opt) addHoverField(opt)
                                    }}
                                    disabled={availableHoverOptions.length === 0}
                                    variant="ghost"
                                    customStyle={{ width: 220 }}
                                    triggerStyle={{ border: '1px solid var(--color-border-default)' }}
                                    placeholderStyle={{ color: 'var(--color-text-primary)' }}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Data fields in Newsfeed export ────────────────────────── */}
            <div className="settings-panel__section">
                <span className="body-m-bold settings-panel__section-title">Data fields in Newsfeed export</span>
                <div className="settings-panel__item">
                    <div className="settings-panel__item-info">
                        <span className="body-m-semibold settings-panel__item-title">
                            Add fields to Newsfeed export
                        </span>
                        <span className="body-s settings-panel__item-desc">
                            Select additional data fields to include in Newsfeed exports
                        </span>
                    </div>
                    <div className="settings-panel__item-action">
                        <SelectionControl
                            type="toggle"
                            checked={state.export_fields_enabled}
                            onChange={e => set({ export_fields_enabled: e.target.checked })}
                        />
                    </div>
                </div>

                {state.export_fields_enabled && (
                    <div className="hover-fields">
                        <div className="hover-fields__tags">
                            {state.newsfeed_export_fields.map(f => (
                                <HoverCardField key={f.key} field={f} onRemove={removeExportField} />
                            ))}
                        </div>
                        {state.newsfeed_export_fields.length < 30 && (
                            <SelectBox
                                Size="Large"
                                value={undefined}
                                placeholder={availableExportOptions.length === 0 ? "No more fields" : "Select a field to add"}
                                options={availableExportOptions.map(o => ({ label: o.label, value: o.key }))}
                                handleOnChange={(val) => {
                                    const opt = availableExportOptions.find(o => o.key === val)
                                    if (opt) addExportField(opt)
                                }}
                                disabled={availableExportOptions.length === 0}
                                variant="ghost"
                                customStyle={{ width: 220 }}
                                triggerStyle={{ border: '1px solid var(--color-border-default)' }}
                                placeholderStyle={{ color: 'var(--color-text-primary)' }}
                            />
                        )}
                    </div>
                )}
            </div>

            </>}
        </div>
    )
}

export default ConfigurationTab
